<script setup lang="ts">
import { computed, onMounted, toRef } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import type { ChartCard } from "@/types";
import { Select } from "@/components/episto-ui/select";
import { Input } from "@/components/ui/input";
import ColorToggleField from "./CardOptionFields/ColorToggleField.vue";
import PopulationField from "./CardOptionFields/PopulationField.vue";
import { GEO_COUNTRIES } from "./constants";
import { cardCodes, useAnalysisContext, useCardPopulation } from "./analysisData";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";

const props = defineProps({
  card: { type: Object, required: true },
});

const card = toRef(props, "card");
const store = useResultsV2Store();
const cardsStore = useResultsV2CardsStore();
const { allQuestionCards } = storeToRefs(store);
const { localOptions, updateLocalOption } = useCardLocalOptions(card);
const { availableCards, displayLocale } = useAnalysisContext();
const { color: populationColor } = useCardPopulation(card);

onMounted(() => store.fetchAllQuestionCards());

const AVERAGE_TYPES = ["q_rating", "q_nps", "q_slider", "q_numeric"];
const SHARE_TYPES = ["q_single", "q_multiple", "q_image_choice", "q_image_choice_multiple", "q_hidden"];
const NO_METRIC = "none";
const DEFAULT_MIN_BASE = 5;
const ZIP_TYPES = ["q_zipcode", "q_numeric"];

const toOption = (c: ChartCard) => ({ value: c.questions?.[0]?.id, label: c.name || c.token });

const zipOptions = computed(() =>
  (allQuestionCards.value as ChartCard[])
    .filter((c) => ZIP_TYPES.includes(c.type || ""))
    .map(toOption)
    .filter((option) => option.value != null),
);

const metricOptions = computed(() => [
  { value: NO_METRIC, label: "None, respondent count" },
  ...(availableCards.value as ChartCard[])
    .filter((c) => AVERAGE_TYPES.includes(c.type || "") || SHARE_TYPES.includes(c.type || ""))
    .map(toOption)
    .filter((option) => option.value != null),
]);

function setContent(patch: Record<string, unknown>) {
  cardsStore.updateCardLocal(props.card.id, { content: { ...props.card.content, ...patch } });
}

const zipQuestion = computed({
  get: () => props.card.content?.question_id || null,
  set: (value: number | null) => setContent({ question_id: value }),
});

const country = computed({
  get: () => props.card.content?.country || GEO_COUNTRIES[0].value,
  set: (value: string) => setContent({ country: value }),
});

const metricQuestion = computed({
  get: () => props.card.content?.metric_question_id || NO_METRIC,
  set: (value: number | string) => setContent({ metric_question_id: value === NO_METRIC ? null : value, metric_codes: [] }),
});

const metricMeta = computed(() => {
  const id = props.card.content?.metric_question_id;
  if (!id) return null;
  return (availableCards.value as ChartCard[]).find((c) => (c.questions || []).some((q) => q.id === id)) || null;
});

const isShareMetric = computed(() => !!metricMeta.value && SHARE_TYPES.includes(metricMeta.value.type || ""));
const hasMetric = computed(() => metricQuestion.value !== NO_METRIC);

const answerOptions = computed(() => {
  const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));
  return cardCodes(metricMeta.value, resolveTitle).map(({ code, label }) => ({ value: code, label }));
});

const metricCodes = computed({
  get: () => props.card.content?.metric_codes || [],
  set: (value: string[] | null) => setContent({ metric_codes: value || [] }),
});

const minBase = computed({
  get: () => props.card.content?.min_base ?? DEFAULT_MIN_BASE,
  set: (value: string | number) => setContent({ min_base: Math.max(1, parseInt(String(value), 10) || DEFAULT_MIN_BASE) }),
});

const accentColor = computed({
  get: () => localOptions.value.color || null,
  set: (value: string | null) => updateLocalOption("color", value),
});
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <OptionRow label="Zipcode question">
      <Select
        :model-value="zipQuestion"
        :options="zipOptions"
        placeholder="Select a zipcode question..."
        filterable
        @update:model-value="zipQuestion = $event" />
    </OptionRow>

    <OptionRow
      v-if="GEO_COUNTRIES.length > 1"
      label="Country">
      <Select
        :model-value="country"
        :options="GEO_COUNTRIES"
        @update:model-value="country = $event" />
    </OptionRow>

    <OptionRow
      label="Color by"
      hint="Respondent count by default, the average of a numeric question, or the share of respondents who picked given answers.">
      <Select
        :model-value="metricQuestion"
        :options="metricOptions"
        filterable
        @update:model-value="metricQuestion = $event" />
    </OptionRow>

    <OptionRow
      v-if="isShareMetric"
      label="Answers to map"
      nested>
      <Select
        :model-value="metricCodes"
        :options="answerOptions"
        placeholder="Select answers..."
        multiple
        filterable
        @update:model-value="metricCodes = $event" />
    </OptionRow>

    <OptionRow
      v-if="hasMetric"
      label="Minimum base"
      hint="Areas where fewer respondents answered the coloring question stay gray: a value over a handful of people is noise."
      nested>
      <Input
        type="number"
        min="1"
        :model-value="minBase"
        @update:model-value="minBase = $event" />
    </OptionRow>

    <PopulationField :card="card" />
  </OptionSection>

  <OptionSection
    id="display"
    title="Display"
    :count="accentColor ? 1 : 0">
    <ColorToggleField
      v-model="accentColor"
      :default-color="populationColor"
      label="Map color"
      hint="Color of the areas. It starts as the color of its population." />
  </OptionSection>
</template>
